import { Info } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCreatePkgReq } from '@/api/package-req/mutations/useCreatePkgReq'
import { PkgReqTypeEnum } from '@/api/package-req/types/PkgReq'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs'
import { AppTooltip } from '@/components/AppTooltip'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { SegmentedButton } from '@/components/SegmentedButton'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { WarehouseFilter } from '@/components/WarehouseFilter'
import calcWeight from '@/helpers/CalculationFunction'
import { isPkgUniteable } from '@/helpers/pkgUtils'
import { useToast } from '@/hooks/useToast'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { UniteConfirmationDialog } from './cmps/UniteConfirmationDialog'
import { UniteSuccessDialog } from './cmps/UniteSuccessDialog'

type ViewType = 'all' | 'split'

const segmentedOptions = [
  { value: 'all', label: 'כל החבילות' },
  { value: 'split', label: 'חבילות מפוצלות' },
]
const _uniteWeightCheck = (packagesForUnite: UserPkg[]) => {
  const finalWeight = packagesForUnite.reduce(
    (acc, p) => (acc += calcWeight(p.dimensions.width, p.dimensions.length, p.dimensions.height, p.dimensions.weight)),
    0
  )

  if (finalWeight >= 20) return true
  else return false
}

const _uniteFlammableCheck = (packagesForUnite: UserPkg[]) => {
  let isFlammable = null
  let isNormal = null
  for (const p of packagesForUnite) {
    if (p.isFlammable) isFlammable = true
    if (!p.isFlammable) isNormal = true

    if ((isFlammable !== null && isNormal !== null) || (isFlammable && isNormal)) {
      return true
    }
  }
  return false
}
export default function UnitePkgPage() {
  const [selectedView, setSelectedView] = useState<ViewType>('all')
  const { data: userPkgs, isLoading: isLoadingUserPkgs } = useUserPkgs()
  const [selectedWarehouses, setSelectedWarehouses] = useState<PkgWarehouse>(userPkgs?.[0]?.warehouse ?? PkgWarehouse.GR)
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([])
  const [uniteErrorDialogOpen, setUniteErrorDialogOpen] = useState<boolean>(false)
  const [uniteConfirmationDialogOpen, setUniteConfirmationDialogOpen] = useState<boolean>(false)
  const [isCreatingUniteRequest, setIsCreatingUniteRequest] = useState<boolean>(false)
  const { showCustomToast } = useToast()
  const { mutateAsync: createPackageReqMutation } = useCreatePkgReq()

  const isSplitPkg = useCallback(
    (pkg: UserPkg) => {
      // If a package shares the same tracking number with another package, it is free to unite
      return userPkgs?.some(p => p.fromTrackingNumber === pkg.fromTrackingNumber && p.id !== pkg.id)
    },
    [userPkgs]
  )
  const uniteablePackages = useMemo(() => {
    return userPkgs?.filter(isPkgUniteable)
  }, [userPkgs])

  const pkgsByView = useMemo(() => {
    return uniteablePackages?.filter(pkg => {
      if (selectedView === 'split' && !isSplitPkg(pkg)) {
        return false
      }
      return true
    })
  }, [uniteablePackages, selectedView, isSplitPkg])

  const filteredPackages = useMemo(
    () =>
      pkgsByView?.filter(pkg => {
        if (selectedWarehouses && pkg.warehouse !== selectedWarehouses) {
          return false
        }
        return true
      }),
    [pkgsByView, selectedWarehouses]
  )
  const availableWarehouses = useMemo(() => {
    const warehouseToPkgs = pkgsByView?.reduce(
      (acc, pkg) => {
        acc[pkg.warehouse] = (acc[pkg.warehouse] || 0) + 1
        return acc
      },
      {} as Record<PkgWarehouse, number>
    )
    return Object.keys(warehouseToPkgs || {}).filter(warehouse => warehouseToPkgs![warehouse as PkgWarehouse] > 1) as PkgWarehouse[]
  }, [pkgsByView])

  useEffect(() => {
    if (availableWarehouses.length) {
      setSelectedWarehouses(availableWarehouses[0])
    }
  }, [availableWarehouses])
  const handlePackageSelect = (pkgId: string) => {
    setSelectedPackageIds(prev => (prev.includes(pkgId) ? prev.filter(id => id !== pkgId) : [...prev, pkgId]))
  }

  const uniteErrorMessage = useMemo(() => {
    const packagesForUnite = selectedPackageIds.map(pId => pkgsByView!.find(p => p.id === pId)!).filter(Boolean)
    if (packagesForUnite.length < 2) {
      return 'יש לבחור לפחות 2 חבילות'
    }
    if (_uniteWeightCheck(packagesForUnite)) {
      return 'החבילה כבדה מדי או גדולה מדי. לא ניתן לאחד חבילות שמשקלן הכולל, או משקלן הנפחי, הוא מעל 20 ק"ג'
    }
    if (_uniteFlammableCheck(packagesForUnite)) {
      return 'לא ניתן לאחד חבילת נוזלים עם חבילות רגילות'
    }
    return ''
  }, [pkgsByView, selectedPackageIds])

  const handleContinue = async () => {
    if (selectedPackageIds.length < 2) return
    if (uniteErrorMessage) {
      setUniteErrorDialogOpen(true)
      return
    }
    // Show confirmation dialog
    setUniteConfirmationDialogOpen(true)
  }

  const handleConfirmUnite = async (shouldShrink?: boolean) => {
    setIsCreatingUniteRequest(true)
    try {
      const packagesForUnite = selectedPackageIds.map(pId => pkgsByView!.find(p => p.id === pId)).filter(Boolean) as UserPkg[]
      // Create unite package request
      await createPackageReqMutation({
        reqType: PkgReqTypeEnum.UniteRequest,
        unitePackageIds: selectedPackageIds,
        shouldShrink,
      })

      // Close confirmation dialog and show success dialog
      setUniteConfirmationDialogOpen(false)
      const isFreeUnite = getIsFreeUnite(packagesForUnite)
      const uniteFee = isFreeUnite ? 0 : packagesForUnite.length * 1.5
      const shrinkFee = shouldShrink ? 5 : 0
      showCustomToast(close => (
        <UniteSuccessDialog packageCount={packagesForUnite.length} uniteFee={uniteFee} shrinkFee={shrinkFee} onClose={close} />
      ))
    } catch (error) {
      console.error('Failed to create unite request:', error)
      // Handle error - could show error dialog
    } finally {
      setIsCreatingUniteRequest(false)
    }
  }

  const resetSelections = () => {
    setSelectedPackageIds([])
    setSelectedWarehouses(pkgsByView?.[0]?.warehouse ?? PkgWarehouse.GR)
  }
  const getIsFreeUnite = (pkgs: UserPkg[]) => {
    return pkgs.length > 0 && pkgs.every(p => pkgs[0].fromTrackingNumber === p.fromTrackingNumber)
  }
  const isSelectedPackagesFreeUnite = useMemo(() => {
    return getIsFreeUnite(selectedPackageIds.map(pId => pkgsByView!.find(p => p.id === pId)).filter(Boolean) as UserPkg[])
  }, [selectedPackageIds, pkgsByView])

  return (
    <div className='mx-auto max-w-5xl p-6'>
      {/* Breadcrumb */}
      <div className='mb-4'>
        <AppBreadcrumbs
          steps={[
            { label: 'החבילות שלי', to: RoutePath.ClientPkg },
            { label: 'איחוד חבילות', to: RoutePath.ClientPkgUnite },
          ]}
        />
      </div>
      {/* Header */}
      <div className='mb-8'>
        <h1 className='text-text-primary mb-2 text-xl font-medium'>בחירת חבילות לאיחוד</h1>
        <div className='text-text-secondary text-sm leading-5'>
          <p className='mb-1'>כאן תוכל לאחד את החבילות שלך ולחסוך בדמי משלוח-</p>
          <p className='mb-1'>שים לב, המשקל המוצג הוא המשקל לחיוב והוא הגבוה מבין נפח או שווי החבילה</p>
          <AppTooltip
            disableHover
            triggerProps={{
              className: 'text-text-link underline',
            }}
            side='left'
            contentClassName='text-text-secondary bg-white text-center text-sm shadow-md'
            content={
              <div className='flex flex-col gap-3'>
                <p className='font-medium'>מהו משקל לחיוב?</p>
                <p>
                  בחישוב עלויות משלוח, חברת השילוח בוחרת את הערך הגבוה יותר מבין משקל פיזי ומשקל נפחי (נפח החבילה ביחס למידות). לכן, גם אם
                  החבילה קלה אבל גדולה – ייתכן שתחויב לפי המשקל הנפחי.
                </p>
              </div>
            }
          >
            תסבירו לי עוד
          </AppTooltip>
        </div>
      </div>
      {/* Filters Section */}
      <div className='mb-6 space-y-4'>
        {/* Segmented Control */}
        <SegmentedButton
          options={segmentedOptions}
          value={selectedView}
          onChange={value => {
            resetSelections()
            setSelectedView(value as ViewType)
          }}
          className='w-full max-w-2xl'
        />

        {/* Info Box */}
        <div className='flex items-start gap-2 rounded-2xl bg-white p-4'>
          <Info className='text-text-secondary mt-0.5 h-4 w-4 flex-shrink-0' />
          <p className='text-text-secondary text-right text-sm'>יש לבחור לפחות 2 חבילות מאותה המדינה בלבד.</p>
        </div>

        {/* Warehouse Filter */}
        <WarehouseFilter
          singleSelect
          warehouses={availableWarehouses}
          value={[selectedWarehouses]}
          onChange={newVal => {
            resetSelections()
            setSelectedWarehouses(newVal[0])
          }}
        />
      </div>
      {/* Package List */}
      <div className='mb-8 space-y-4'>
        {isLoadingUserPkgs ? (
          <Spinner />
        ) : (
          filteredPackages?.map(pkg => (
            <PkgPreview
              additionalStatus={isSplitPkg(pkg) ? 'חבילה מפוצלת' : undefined}
              key={pkg.id}
              pkg={pkg}
              currentView='all'
              isSelected={selectedPackageIds.includes(pkg.id)}
              onSelect={handlePackageSelect}
              hideActions={true}
            />
          ))
        )}
      </div>
      {/* Continue Button */}
      <div className='flex justify-center'>
        <Button
          onClick={handleContinue}
          disabled={selectedPackageIds.length < 2}
          className='bg-button-primary-default text-button-primary-text rounded-full px-6 py-3'
        >
          המשך לאיחוד חבילות
        </Button>
      </div>
      <ConfirmDialog
        isOpen={uniteErrorDialogOpen}
        onClose={() => setUniteErrorDialogOpen(false)}
        cancelText='הבנתי'
        title='אי אפשר לאחד את החבילות האלה'
        content={uniteErrorMessage}
      />

      {/* Unite Confirmation Dialog */}
      <UniteConfirmationDialog
        isFreeUnite={isSelectedPackagesFreeUnite}
        isOpen={uniteConfirmationDialogOpen}
        onClose={() => setUniteConfirmationDialogOpen(false)}
        onConfirm={handleConfirmUnite}
        isLoading={isCreatingUniteRequest}
        isUSWarehouse={selectedWarehouses === PkgWarehouse.USA}
        packageCount={selectedPackageIds.length}
      />
    </div>
  )
}
